import type { SessionAuthMethod } from "@repo/app-types/api/user";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { FC } from "react";
import { AuthMethodOptions } from "./authMethodOptions.js";
import { InvitationMagicLinkConfirm } from "./invitationMagicLinkConfirm.js";
import { SignInPageShell } from "./signInPageShell.js";

export type InvitationSignInStatus = "loading" | "ready" | "invalid";

/** Which surface the ready screen shows: the method picker or the magic link. */
export type InvitationSignInMode = "options" | "magicLink";

export interface InvitationSignInScreenProps {
  status: InvitationSignInStatus;
  mode: InvitationSignInMode;
  companyName: string | null;
  acceptedMethods: readonly SessionAuthMethod[];
  ssoProviderIds: readonly string[];
  /** Masked invited address (e.g. `j••••@acme.com`), shown as a hint. */
  maskedEmail: string | null;
  isSendingMagicLink: boolean;
  isMagicLinkSent: boolean;
  errorMessage: string | null;
  onGoogleSignIn: () => void;
  onMicrosoftSignIn: () => void;
  onSsoSignIn: () => void;
  /** Switch to the magic-link surface. */
  onSelectMagicLink: () => void;
  /** Return from the magic-link surface to the method picker. */
  onBackToOptions: () => void;
  /** Send the magic link to the invited address (server-side). */
  onSendMagicLink: () => void;
  onResetMagicLink: () => void;
  /** Navigate to standard sign-in when the invitation link can't be used. */
  onGoToSignIn: () => void;
}

/**
 * Dedicated pre-auth screen for accepting a company invitation. Shows the
 * inviting company and only the sign-in methods that company accepts (via the
 * shared {@link AuthMethodOptions}), so the user never picks a method that would
 * be rejected after signing in. A masked hint of the invited address nudges the
 * user toward the matching identity for any method. Email is a mode-switch
 * button that reveals the magic-link confirmation. Presentational — the
 * container owns the fetch and the auth side effects.
 */
export const InvitationSignInScreen: FC<InvitationSignInScreenProps> = ({
  status,
  mode,
  companyName,
  acceptedMethods,
  ssoProviderIds,
  maskedEmail,
  isSendingMagicLink,
  isMagicLinkSent,
  errorMessage,
  onGoogleSignIn,
  onMicrosoftSignIn,
  onSsoSignIn,
  onSelectMagicLink,
  onBackToOptions,
  onSendMagicLink,
  onResetMagicLink,
  onGoToSignIn,
}) => {
  if (status === "loading") {
    return (
      <SignInPageShell>
        <DefaultSkeleton />
      </SignInPageShell>
    );
  }

  if (status === "invalid") {
    return (
      <SignInPageShell>
        <h2 className="mb-2 text-center text-2xl font-semibold text-foreground">
          This invitation can&apos;t be opened
        </h2>
        <p className="mb-4 text-center text-sm text-gray-600">
          This invitation link is invalid or has expired. Ask your admin to
          invite you again, or sign in if you already have an account.
        </p>
        <Button
          className="mx-auto"
          variant={ButtonVariant.Primary}
          onClick={onGoToSignIn}
        >
          Go to sign in
        </Button>
      </SignInPageShell>
    );
  }

  const isMagicLinkMode = mode === "magicLink";

  // Mirror the standard sign-in screen: a "Back" link below the card returns
  // from the magic-link surface to the method picker (hidden once a link is sent).
  const backLink =
    isMagicLinkMode && !isMagicLinkSent ? (
      <div className="mt-4 text-center">
        <button
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
          onClick={onBackToOptions}
        >
          Back
        </button>
      </div>
    ) : null;

  return (
    <SignInPageShell belowCard={backLink}>
      <h2 className="mb-2 text-center text-2xl font-semibold text-foreground">
        {companyName
          ? `Join the ${companyName} team on Alpharun`
          : "Accept your invitation"}
      </h2>
      <div className="mb-4 text-center text-sm text-gray-600">
        Sign in to accept your invitation.
        {!isMagicLinkMode && maskedEmail ? (
          <>
            <br />
            Invited as{" "}
            <span className="font-medium text-foreground">{maskedEmail}</span>.
          </>
        ) : null}
      </div>
      {isMagicLinkMode ? (
        <InvitationMagicLinkConfirm
          maskedEmail={maskedEmail}
          isSending={isSendingMagicLink}
          isSent={isMagicLinkSent}
          onSend={onSendMagicLink}
          onReset={onResetMagicLink}
        />
      ) : (
        <div className="flex flex-col items-center space-y-4">
          <AuthMethodOptions
            acceptedMethods={acceptedMethods}
            ssoProviderIds={ssoProviderIds}
            isSignUp
            onGoogleSignIn={onGoogleSignIn}
            onMicrosoftSignIn={onMicrosoftSignIn}
            onSsoSignIn={onSsoSignIn}
            onEmailSignIn={onSelectMagicLink}
          />
        </div>
      )}
      {errorMessage ? (
        <p className="mt-4 text-center text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}
    </SignInPageShell>
  );
};
